import { VStack, Link, HStack, useColorModeValue } from "@chakra-ui/react";
import { CellContext, ColumnDef, createColumnHelper } from "@tanstack/react-table";
import CDS from "design-system";
import { useMemo, useCallback } from "react";
import { Link as RouterLink } from "react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import GenericTable, { GenericTableProps } from "components/Table/GenericTable";
import type { ResponseModel } from "services/ApiClient/responseModel";
import useQueryString from "hooks/useQueryString";
import { INTERNAL_TEAM_ROUTES, SHARED_ROUTES } from "definitions/constants/routeConstants";
import userBatcher from "features/User/batcher";
import { User } from "features/User/models";
import { titleize } from "helpers/string";
import { dateInFormat } from "helpers/time";
import { generatePath } from "helpers/url";
import apiClient from "services/ApiClient";
import { apiCheckpointById, apiEmailById, apiMeetingById, apiMessageById, apiTaskById } from "helpers/url/api/v2";
import { apiIssuePathById } from "helpers/url/pages/v2/issuepath";
import { actionPathByPrefixId } from "helpers/url/pages/v2/actionPaths";
import resolveIssueType, { expandedIssueTypeResolver } from "features/Issues/Resolutions/resolveIssueType";
import { PrefixId, ActionType } from "features/Resources/Shared/models";
import { ActionIconTag } from "design-system/components/ActionIconTag";
import { TaskStatusTag } from "design-system/components/TaskStatusTag";
import type { StatusType } from "design-system/components/TaskStatusTag";
import { CheckpointStatusTag } from "design-system/components/CheckpointStatusTag";
import type { CheckpointStatusType } from "design-system/components/CheckpointStatusTag";
import { AttendeeAvatar } from "components/Tag/ResourceTargetTag/MeetingAttendeeTag";
import { PlatformEvent, PlatformEventFilters } from "../types";
import { platformEventKeys } from "../hooks";
import { fetchPlatformEvents } from "../api";

const columnHelper = createColumnHelper<PlatformEvent>();

// Generate entity URL based on type
function getEntityUrl(entityType: string | null, entityId: string | null): string | null {
  if (!entityType || !entityId) return null;

  switch (entityType) {
    case "Task":
      return generatePath({ path: SHARED_ROUTES.tasks.show, params: { id: entityId } });
    case "Email":
      return generatePath({ path: SHARED_ROUTES.emails.show, params: { id: entityId } });
    case "Message":
      return generatePath({ path: SHARED_ROUTES.messages.show, params: { id: entityId } });
    case "Meeting":
      return generatePath({ path: SHARED_ROUTES.meetings.show, params: { id: entityId } });
    case "NewHireJourney":
      return generatePath({ path: INTERNAL_TEAM_ROUTES.newHireJourneys.show, params: { id: entityId } });
    default:
      return null;
  }
}

// Get entity display name from meta or type
function humanizeEntityType(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}

const ENTITY_NAME_META_KEYS = ["entityName", "entity_name", "title", "name", "subject"];
const ISSUE_TYPE_META_KEYS = ["issueType", "issue_type"];

function getFirstMetaString(meta: PlatformEvent["meta"], keys: string[]): string | null {
  const matchKey = keys.find((key) => {
    const value = meta[key];
    return typeof value === "string" && value.trim().length > 0;
  });
  return matchKey ? (meta[matchKey] as string) : null;
}

function getEntityDisplayName(event: PlatformEvent, overrideName?: string | null): string {
  const { entityType, meta } = event;

  if (overrideName) return overrideName;

  // First try the explicit entity_name field
  const explicitName = getFirstMetaString(meta, [...ENTITY_NAME_META_KEYS]);
  if (explicitName) return explicitName;

  const issueType = getFirstMetaString(meta, ISSUE_TYPE_META_KEYS);
  if (issueType) return titleize(issueType);

  // Fall back to other meta fields
  const taskAction = meta.taskAction ?? meta.task_action;
  if (taskAction) return String(taskAction).replace(/_/g, " ");

  // Fall back to entity type label
  return entityType ? humanizeEntityType(entityType) : "Unknown";
}

function buildEntityKey(entityType: string, entityId: string) {
  return `${entityType}:${entityId}`;
}

function hasMetaEntityName(meta: PlatformEvent["meta"]) {
  return Boolean(getFirstMetaString(meta, ENTITY_NAME_META_KEYS) || getFirstMetaString(meta, ISSUE_TYPE_META_KEYS));
}

function getEntityTitleFromRecord(entityType: string, record: any): string | null {
  if (!record) return null;
  switch (entityType) {
    case "Email":
      return record.subject || record.name || null;
    case "Meeting":
    case "Task":
    case "Message":
      return record.name || null;
    case "Issue":
      return record.issueType ? titleize(record.issueType) : null;
    default:
      return null;
  }
}

type EntityDetailQuery = {
  queryKey: string[];
  queryFn: () => Promise<unknown>;
  staleTime: number;
  enabled?: boolean;
};

function buildEntityDetailQuery(entityType: string, entityId: string): EntityDetailQuery {
  const baseQuery = {
    queryKey: ["platform-events", "entity", entityType, entityId],
    staleTime: 1000 * 60 * 3,
  };

  switch (entityType) {
    case "Email":
      return {
        ...baseQuery,
        queryFn: async () => apiClient.get<unknown>(apiEmailById(entityId)),
      };
    case "Meeting":
      return {
        ...baseQuery,
        queryFn: async () => apiClient.get<unknown>(apiMeetingById(entityId)),
      };
    case "Message":
      return {
        ...baseQuery,
        queryFn: async () => apiClient.get<unknown>(apiMessageById(entityId)),
      };
    case "Task":
      return {
        ...baseQuery,
        queryFn: async () => apiClient.get<unknown>(apiTaskById(entityId)),
      };
    case "Checkpoint":
      return {
        ...baseQuery,
        queryFn: async () => apiClient.get<unknown>(apiCheckpointById(entityId)),
      };
    case "Issue":
      return {
        ...baseQuery,
        queryFn: async () => apiClient.get<unknown>(apiIssuePathById(entityId)),
      };
    default:
      return {
        ...baseQuery,
        enabled: false,
        queryFn: async () => null,
      };
  }
}

function getIssueActionPrefixId(action: any): string | null {
  return action?.prefixId || action?.prefix_id || null;
}

function getIssueActionTitle(action: any): string | null {
  return action?.subject || action?.name || action?.title || null;
}

function getIssueActionLabel(action: any, actionCount: number): string | null {
  const baseTitle = getIssueActionTitle(action);
  if (!baseTitle) return null;
  if (actionCount <= 1) return baseTitle;
  return `${baseTitle} (+${actionCount - 1} more)`;
}

function getIssueDescription(issueDetail: any): string | null {
  if (!issueDetail) return null;
  try {
    const expandedResolver = expandedIssueTypeResolver(issueDetail);
    if (expandedResolver) {
      return expandedResolver(issueDetail) || null;
    }
    return resolveIssueType(issueDetail) || null;
  } catch {
    return null;
  }
}

// Get action type from entity type
function getActionTypeFromEntityType(entityType: string | null): ActionType | null {
  if (!entityType) return null;
  return entityType.toLowerCase() as ActionType;
}

// Check if item should show person icon (for Item column)
function shouldShowPersonIcon(entityType: string | null, eventName: string, meta?: PlatformEvent["meta"]): boolean {
  // New hire journeys always show person icon
  if (entityType === "NewHireJourney") {
    return true;
  }

  // Check for person-related keywords in event name or meta
  const personIssueKeywords = [
    "missing",
    "unassigned",
    "user",
    "contact",
    "manager",
    "buddy",
    "assignee",
    "role",
    "profile",
    "slack",
  ];
  const lowerEventName = eventName.toLowerCase();

  // Check event name for person-related keywords
  if (personIssueKeywords.some((keyword) => lowerEventName.includes(keyword))) {
    return true;
  }

  // Check meta for person-related issue types
  if (meta) {
    const issueType = meta.issueType || meta.issue_type;
    if (issueType && typeof issueType === "string") {
      const lowerIssueType = issueType.toLowerCase();
      if (personIssueKeywords.some((keyword) => lowerIssueType.includes(keyword))) {
        return true;
      }
    }
  }

  return false;
}

// Get status type for Details column - defaults to "active" if no specific status found
function getStatusFromEventName(eventName: string): StatusType {
  // Issue-related events
  if (eventName.startsWith("issue.")) {
    if (eventName === "issue.resolved") {
      return "completed";
    }
    return "issue";
  }

  // Failed states (email failed, message failed, etc.)
  if (eventName.includes("failed")) {
    return "issue";
  }

  // Completed/resolved states
  if (eventName.includes("completed") || eventName.includes("resolved")) {
    return "completed";
  }

  // Skipped state
  if (eventName.includes("skipped")) {
    return "skipped";
  }

  // Blocked state
  if (eventName.includes("blocked")) {
    return "blocked";
  }

  // Overdue state
  if (eventName.includes("overdue")) {
    return "overdue";
  }

  // Urgent state
  if (eventName.includes("urgent")) {
    return "urgent";
  }

  // Checkpoint-specific statuses
  if (eventName.startsWith("checkpoint.")) {
    if (eventName.includes("notification.sent")) return "sent" as StatusType;
    if (eventName.includes("evaluated")) return "evaluated" as StatusType;
    if (eventName.includes("complete")) return "completed";
    if (eventName.includes("partial")) return "overdue";
    if (eventName.includes("behind")) return "urgent";
  }

  // Sent/notification events — treat as completed (neutral checkmark)
  if (eventName.includes("sent") || eventName.includes("notification")) {
    return "completed";
  }

  // Default to active for all other events
  return "active";
}

function renderEntityCell(
  info: CellContext<PlatformEvent, unknown>,
  entityNameOverrides: Map<string, string>,
  issueDetailsMap: Map<string, any>,
  linkColor: string,
) {
  const event = info.row.original;
  const issueDetail = event.entityType === "Issue" && event.entityId ? issueDetailsMap.get(event.entityId) : null;

  const actionType = getActionTypeFromEntityType(event.entityType);
  const showPersonIcon = shouldShowPersonIcon(event.entityType, event.eventName, event.meta);

  if (issueDetail) {
    const actions = issueDetail.actions || [];
    const primaryAction = actions[0];
    const actionPrefixId = getIssueActionPrefixId(primaryAction);
    const actionLabel = getIssueActionLabel(primaryAction, actions.length);
    const actionUrl = actionPrefixId ? actionPathByPrefixId(actionPrefixId as PrefixId) : null;

    if (actionLabel && actionUrl) {
      return (
        <HStack spacing={2} align="center">
          {showPersonIcon ? (
            <ActionIconTag.Icon actionType="person" size="md" />
          ) : (
            actionType && <ActionIconTag.Icon actionType={actionType} size="md" />
          )}
          <Link
            as={RouterLink}
            to={actionUrl}
            fontSize="sm"
            color={linkColor}
            _hover={{ textDecoration: "underline" }}
            noOfLines={2}
            maxW="100%"
          >
            {actionLabel}
          </Link>
        </HStack>
      );
    }

    if (actionLabel) {
      return (
        <HStack spacing={2} align="center">
          {showPersonIcon ? (
            <ActionIconTag.Icon actionType="person" size="md" />
          ) : (
            actionType && <ActionIconTag.Icon actionType={actionType} size="md" />
          )}
          <CDS.Text12 color="fg.default" noOfLines={2} maxW="100%">
            {actionLabel}
          </CDS.Text12>
        </HStack>
      );
    }
  }

  const url = getEntityUrl(event.entityType, event.entityId);
  const overrideName =
    event.entityType && event.entityId
      ? entityNameOverrides.get(buildEntityKey(event.entityType, event.entityId))
      : null;
  const displayName = getEntityDisplayName(event, overrideName);

  if (!url) {
    return (
      <HStack spacing={2} align="center">
        {showPersonIcon ? (
          <ActionIconTag.Icon actionType="person" size="md" />
        ) : (
          actionType && <ActionIconTag.Icon actionType={actionType} size="md" />
        )}
        <CDS.Text12
          color={displayName === "Unknown" ? "fg.muted" : "fg.default"}
          fontStyle={displayName === "Unknown" ? "italic" : "normal"}
          noOfLines={2}
          maxW="100%"
        >
          {displayName === "Unknown" ? "—" : displayName}
        </CDS.Text12>
      </HStack>
    );
  }

  return (
    <HStack spacing={2} align="center">
      {showPersonIcon ? (
        <ActionIconTag.Icon actionType="person" size="md" />
      ) : (
        actionType && <ActionIconTag.Icon actionType={actionType} size="md" />
      )}
      <Link
        as={RouterLink}
        to={url}
        fontSize="sm"
        color={linkColor}
        _hover={{ textDecoration: "underline" }}
        noOfLines={2}
        maxW="100%"
      >
        {displayName}
      </Link>
    </HStack>
  );
}

function renderNewHireCell(info: CellContext<PlatformEvent, PlatformEvent["newHire"]>) {
  const newHire = info.getValue();
  const event = info.row.original;
  const journeyId = event.newHireJourneyId;

  if (!newHire) {
    return (
      <CDS.Text12 color="fg.muted" fontStyle="italic">
        —
      </CDS.Text12>
    );
  }

  const avatarContent = (
    <AttendeeAvatar name={newHire.fullName || newHire.firstName} src={newHire.avatarUrl} roleName="new_hire" />
  );

  if (journeyId) {
    return (
      <Link as={RouterLink} to={`/new-hires/${journeyId}`} display="inline-block">
        {avatarContent}
      </Link>
    );
  }

  return avatarContent;
}

function renderOccurredAtCell(
  info: CellContext<PlatformEvent, string>,
  subTextColor: string,
  invalidTextColor: string,
) {
  const dateValue = info.getValue();
  const date = new Date(dateValue);

  // Handle invalid dates
  if (Number.isNaN(date.getTime())) {
    return (
      <CDS.Text12 color={invalidTextColor} fontStyle="italic">
        Invalid date
      </CDS.Text12>
    );
  }

  return (
    <VStack align="start" spacing={0}>
      <CDS.Text12 fontWeight="medium">
        {dateInFormat(date, "MMM D, YYYY")}
      </CDS.Text12>
      <CDS.Text10 color={subTextColor}>
        {dateInFormat(date, "h:mm a")}
      </CDS.Text10>
    </VStack>
  );
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return dateInFormat(date, "MMM D, YYYY");
  } catch {
    return dateString;
  }
}

function formatDateTime(dateString: string | undefined): string {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return dateInFormat(date, "MMM D, h:mm a");
  } catch {
    return dateString;
  }
}

const ACTION_LABELS: Record<string, string> = {
  no_response: "marked no response",
};

const CHECKPOINT_ACTION_LABELS: Record<string, string> = {
  complete: "Good to Go!",
  partial: "Needs a Nudge",
  behind: "Behind Schedule",
  skipped: "Skipped",
  pending: "Not Run Yet",
};

function humanizeSegmentLower(value: string): string {
  return value.replace(/_/g, " ").toLowerCase();
}

function getActionAndEventType(eventName: string) {
  const segments = eventName.split(".");
  const actionSegment = segments[segments.length - 1] || "updated";
  const action = ACTION_LABELS[actionSegment] || humanizeSegmentLower(actionSegment);
  const eventType = segments.slice(0, -1).map(humanizeSegmentLower).join(" ") || "event";
  return { action, eventType };
}

// Check if an event is a status change by looking for status metadata
function isStatusChangeEvent(meta: PlatformEvent["meta"]): boolean {
  return meta.previousStatus != null && meta.newStatus != null;
}

function getPersonName(user: PlatformEvent["actor"]): string {
  if (!user) return "System";
  return user.fullName || `${user.firstName} ${user.lastName}`;
}

function formatUserId(userId: unknown, usersMap: Map<string, User>): string {
  if (!userId) return "None";
  const userIdStr = String(userId);
  const user = usersMap.get(userIdStr);
  if (!user) return `User ${userIdStr}`;
  return user.fullName || `${user.firstName} ${user.lastName}`;
}

const CHANGE_DESCRIPTORS = [
  {
    previousKey: "previousAssigneeId",
    newKey: "newAssigneeId",
    formatter: (value: unknown, usersMap: Map<string, User>) => formatUserId(value, usersMap),
  },
  {
    previousKey: "previousBuddyId",
    newKey: "newBuddyId",
    formatter: (value: unknown, usersMap: Map<string, User>) => formatUserId(value, usersMap),
  },
  {
    previousKey: "previousManagerId",
    newKey: "newManagerId",
    formatter: (value: unknown, usersMap: Map<string, User>) => formatUserId(value, usersMap),
  },
  {
    previousKey: "previousPeopleTeamContactId",
    newKey: "newPeopleTeamContactId",
    formatter: (value: unknown, usersMap: Map<string, User>) => formatUserId(value, usersMap),
  },
  {
    previousKey: "previousDueDate",
    newKey: "newDueDate",
    formatter: (value: unknown) => formatDate(value as string) || "Not set",
  },
  {
    previousKey: "previousStartDate",
    newKey: "newStartDate",
    formatter: (value: unknown) => formatDate(value as string) || "Not set",
  },
  {
    previousKey: "previousStartTime",
    newKey: "newStartTime",
    formatter: (value: unknown) => formatDateTime(value as string) || "Not set",
  },
] as const;

function getChangeValues(meta: PlatformEvent["meta"], usersMap: Map<string, User>) {
  const descriptor = CHANGE_DESCRIPTORS.find((entry) => meta[entry.previousKey] != null || meta[entry.newKey] != null);
  if (!descriptor) return null;

  const from = descriptor.formatter(meta[descriptor.previousKey], usersMap);
  const to = descriptor.formatter(meta[descriptor.newKey], usersMap);
  return {
    from: from || "Not set",
    to: to || "Not set",
  };
}

function renderNewHireReference(event: PlatformEvent, linkColor: string) {
  if (!event.newHire) return null;
  const name = getPersonName(event.newHire);
  const label = `${name}\u2019s Onboarding Journey`;
  const content = (
    <CDS.Text12 as="span" fontWeight="medium">
      {label}
    </CDS.Text12>
  );
  if (event.newHireJourneyId) {
    return (
      <Link
        as={RouterLink}
        to={`/new-hires/${event.newHireJourneyId}`}
        color={linkColor}
        _hover={{ textDecoration: "underline" }}
      >
        {content}
      </Link>
    );
  }
  return content;
}

const CHECKPOINT_STATUS_EVENT_PREFIXES = [
  "checkpoint.complete",
  "checkpoint.partial",
  "checkpoint.behind",
  "checkpoint.skipped",
  "checkpoint.evaluated",
  "checkpoint.notification",
];

function getCheckpointStatus(eventName: string, status: StatusType): CheckpointStatusType | null {
  if (!CHECKPOINT_STATUS_EVENT_PREFIXES.some((prefix) => eventName.startsWith(prefix))) return null;
  if (
    status === "completed" ||
    status === "overdue" ||
    status === "urgent" ||
    status === "skipped" ||
    status === ("evaluated" as StatusType) ||
    status === ("sent" as StatusType)
  ) {
    return status as unknown as CheckpointStatusType;
  }
  return null;
}

function renderDetailsCell(
  info: CellContext<PlatformEvent, string>,
  usersMap: Map<string, User>,
  issueDetailsMap: Map<string, any>,
  linkColor: string,
  showNewHireReference = true,
) {
  const event = info.row.original;
  const status = getStatusFromEventName(event.eventName);

  if (event.eventName.startsWith("issue.")) {
    const issueDetail = event.entityId ? issueDetailsMap.get(event.entityId) : null;
    const description = getIssueDescription(issueDetail) || getFirstMetaString(event.meta, ISSUE_TYPE_META_KEYS);
    const verb = event.eventName === "issue.resolved" ? "resolved" : "created";
    const newHireReference = showNewHireReference ? renderNewHireReference(event, linkColor) : null;

    return (
      <HStack spacing={2} align="center">
        <TaskStatusTag.IconBadge status={status} size="lg" />
        <CDS.Text12 color="fg.default" noOfLines={2}>
          <CDS.Text12 as="span" fontWeight="medium">
            {`Issue ${verb}`}
          </CDS.Text12>
          {newHireReference && (
            <>
              {" "}
              <CDS.Text12 as="span">for</CDS.Text12> {newHireReference}
            </>
          )}
          {description && (
            <>
              {": "}
              <CDS.Text12 as="span">{description}</CDS.Text12>
            </>
          )}
        </CDS.Text12>
      </HStack>
    );
  }

  const actorName = getPersonName(event.actor);
  const { action, eventType } = getActionAndEventType(event.eventName);
  const newHireReference = showNewHireReference ? renderNewHireReference(event, linkColor) : null;
  const changeValues = getChangeValues(event.meta, usersMap);
  const isStatusChange = isStatusChangeEvent(event.meta);
  const checkpointStatus = getCheckpointStatus(event.eventName, status);
  const displayAction = event.entityType === "Checkpoint" ? CHECKPOINT_ACTION_LABELS[action] || action : action;

  return (
    <HStack spacing={2} align="center">
      {checkpointStatus ? (
        <CheckpointStatusTag.IconBadge status={checkpointStatus} size="lg" />
      ) : (
        <TaskStatusTag.IconBadge status={status} size="lg" />
      )}
      <CDS.Text12 color="fg.default" noOfLines={2}>
        <CDS.Text12 as="span" fontWeight="medium">
          {actorName}
        </CDS.Text12>{" "}
        {isStatusChange ? (
          <>
            <CDS.Text12 as="span">updated {eventType} status to</CDS.Text12>{" "}
            <CDS.Text12 as="span" fontWeight="medium">
              &quot;
              {displayAction}
              &quot;
            </CDS.Text12>
          </>
        ) : (
          <>
            <CDS.Text12 as="span">{action}</CDS.Text12> <CDS.Text12 as="span">{eventType}</CDS.Text12>
          </>
        )}
        {newHireReference && (
          <>
            {" "}
            <CDS.Text12 as="span">for</CDS.Text12> {newHireReference}
          </>
        )}
        {changeValues && (
          <>
            {" "}
            <CDS.Text12 as="span">from</CDS.Text12>{" "}
            <CDS.Text12 as="span" fontWeight="medium">
              {changeValues.from}
            </CDS.Text12>{" "}
            <CDS.Text12 as="span">to</CDS.Text12>{" "}
            <CDS.Text12 as="span" fontWeight="medium">
              {changeValues.to}
            </CDS.Text12>
          </>
        )}
      </CDS.Text12>
    </HStack>
  );
}

interface EventsTableProps {
  filters: PlatformEventFilters;
  onFiltersChange: (filters: PlatformEventFilters) => void;
  isCompact?: boolean;
  showNewHireReference?: boolean;
}

/**
 * EventsTable using GenericTable pattern
 * Handles user batching by fetching events data separately (React Query will deduplicate)
 */
export default function EventsTable({
  filters,
  onFiltersChange,
  isCompact = false,
  showNewHireReference = true,
}: EventsTableProps) {
  const linkColor = useColorModeValue("blue.600", "blue.200");
  const headerTextColor = useColorModeValue("gray.700", "gray.100");
  const headerBorderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const cellTextColor = useColorModeValue("gray.800", "gray.100");
  const cellBorderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const hoverRowBg = useColorModeValue("purple.50", "whiteAlpha.100");
  const timeTextColor = useColorModeValue("gray.600", "gray.300");
  const invalidDateTextColor = useColorModeValue("gray.500", "gray.300");

  // Convert PlatformEventFilters to GenericTable format
  const additionalQueries = useMemo(() => {
    const queries: Record<string, string> = {};
    if (filters.eventType) queries.event_type = filters.eventType;
    if (filters.eventName) queries.event_name = filters.eventName;
    if (filters.newHireJourneyId) queries.new_hire_journey_id = filters.newHireJourneyId;
    if (filters.newHireId) queries.new_hire_id = filters.newHireId;
    if (filters.actorId) queries.actor_id = filters.actorId;
    if (filters.entityType) queries.entity_type = filters.entityType;
    if (filters.entityId) queries.entity_id = filters.entityId;
    if (filters.startDate) queries.start_date = filters.startDate;
    if (filters.endDate) queries.end_date = filters.endDate;
    return queries;
  }, [
    filters.eventType,
    filters.eventName,
    filters.newHireJourneyId,
    filters.newHireId,
    filters.actorId,
    filters.entityType,
    filters.entityId,
    filters.startDate,
    filters.endDate,
  ]);

  const meta = useMemo(
    () => ({
      pagination: {
        perPage: filters.perPage || 50,
        initialPage: filters.page || 1,
      },
      sort: [
        {
          sortBy: filters.sortBy || "occurred_at",
          direction: filters.sortOrder || "desc",
        },
      ],
    }),
    [filters.perPage, filters.page, filters.sortBy, filters.sortOrder],
  );

  // Wrapper function that transforms nested sort parameters to flat format
  const fetchDataWithTransformedSort = useCallback((query: string, signal?: AbortSignal) => {
    // Transform sort[0][sort_by]=field&sort[0][direction]=desc
    // to sort_by=field&sort_order=desc
    if (!query || query === "") {
      return fetchPlatformEvents(query, signal);
    }

    // Remove leading ? if present
    const cleanQuery = query.startsWith("?") ? query.substring(1) : query;
    const params = new URLSearchParams(cleanQuery);

    // Extract sort parameters
    const sortBy = params.get("sort[0][sort_by]");
    const sortDirection = params.get("sort[0][direction]");

    // Remove nested sort parameters
    params.delete("sort[0][sort_by]");
    params.delete("sort[0][direction]");

    // Add flat sort parameters
    if (sortBy) {
      params.set("sort_by", sortBy);
    }
    if (sortDirection) {
      params.set("sort_order", sortDirection);
    }

    // Reconstruct query with ? prefix
    const transformedQuery = `?${params.toString()}`;
    return fetchPlatformEvents(transformedQuery, signal);
  }, []);

  // Build query string to match what GenericTable will use for user batching
  // Note: This creates a separate pagination state, but React Query will deduplicate the actual fetch
  const { queryString: builtQueryString } = useQueryString(meta, additionalQueries);

  // Fetch events data for user batching
  // React Query will deduplicate this with GenericTable's internal query since they use the same queryKey pattern
  const { data: eventsResponse } = useQuery<ResponseModel<PlatformEvent[]>>({
    queryKey: [...platformEventKeys.lists(), builtQueryString],
    queryFn: ({ signal }) => fetchPlatformEvents(builtQueryString.length ? `?${builtQueryString}` : "", signal),
    staleTime: 1000 * 60 * 3, // 3 minutes
    // This query is only for user batching - GenericTable handles the actual table data
    // React Query will deduplicate requests with matching query keys
  });

  const events = useMemo(() => eventsResponse?.data || [], [eventsResponse]);

  const missingEntityIds = useMemo(() => {
    const seen = new Set<string>();
    return events.reduce<{ entityType: string; entityId: string }[]>((acc, event) => {
      if (!event.entityType || !event.entityId) return acc;
      if (event.entityType === "Issue") return acc;
      if (hasMetaEntityName(event.meta)) return acc;
      const key = buildEntityKey(event.entityType, event.entityId);
      if (seen.has(key)) return acc;
      seen.add(key);
      acc.push({ entityType: event.entityType, entityId: event.entityId });
      return acc;
    }, []);
  }, [events]);

  const issueEntityIds = useMemo(() => {
    const seen = new Set<string>();
    return events.reduce<string[]>((acc, event) => {
      if (event.entityType !== "Issue" || !event.entityId) return acc;
      if (seen.has(event.entityId)) return acc;
      seen.add(event.entityId);
      acc.push(event.entityId);
      return acc;
    }, []);
  }, [events]);

  const entityDetailQueries = useMemo(
    () => missingEntityIds.map(({ entityType, entityId }) => buildEntityDetailQuery(entityType, entityId)),
    [missingEntityIds],
  );

  const entityDetailResults = useQueries({
    queries: entityDetailQueries,
  });

  const entityNameOverrides = useMemo(
    () =>
      entityDetailResults.reduce((acc, result, index) => {
        const entity = missingEntityIds[index];
        if (!entity || !result?.data) return acc;
        const title = getEntityTitleFromRecord(entity.entityType, result.data);
        if (title) {
          acc.set(buildEntityKey(entity.entityType, entity.entityId), title);
        }
        return acc;
      }, new Map<string, string>()),
    [entityDetailResults, missingEntityIds],
  );

  const issueDetailResults = useQueries({
    queries: issueEntityIds.map((issueId) => ({
      queryKey: ["platform-events", "issue", issueId],
      queryFn: async () => apiClient.get<unknown>(apiIssuePathById(issueId)),
      staleTime: 1000 * 60 * 3,
    })),
  });

  const issueDetailsMap = useMemo(
    () =>
      issueDetailResults.reduce((acc, result, index) => {
        const issueId = issueEntityIds[index];
        if (issueId && result?.data) {
          acc.set(issueId, result.data);
        }
        return acc;
      }, new Map<string, any>()),
    [issueDetailResults, issueEntityIds],
  );

  const userIds = useMemo(() => {
    const ids = new Set<string>();
    events.forEach((event) => {
      const { meta: eventMeta } = event;
      if (eventMeta.previousAssigneeId) ids.add(String(eventMeta.previousAssigneeId));
      if (eventMeta.newAssigneeId) ids.add(String(eventMeta.newAssigneeId));
      if (eventMeta.previousBuddyId) ids.add(String(eventMeta.previousBuddyId));
      if (eventMeta.newBuddyId) ids.add(String(eventMeta.newBuddyId));
      if (eventMeta.previousManagerId) ids.add(String(eventMeta.previousManagerId));
      if (eventMeta.newManagerId) ids.add(String(eventMeta.newManagerId));
      if (eventMeta.previousPeopleTeamContactId) ids.add(String(eventMeta.previousPeopleTeamContactId));
      if (eventMeta.newPeopleTeamContactId) ids.add(String(eventMeta.newPeopleTeamContactId));
    });
    return Array.from(ids);
  }, [events]);

  // Batch fetch users by IDs
  const { data: usersData } = useQuery<Record<string, User>>({
    queryKey: ["users", "batch", userIds.sort().join(",")],
    queryFn: async () => {
      if (userIds.length === 0) return {};
      const users: Record<string, User> = {};
      await Promise.all(
        userIds.map(async (id) => {
          try {
            const user = await userBatcher.fetch({ id: id });
            if (user) users[id] = user;
          } catch {
            // User not found, skip
          }
        }),
      );
      return users;
    },
    enabled: userIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Create a Map for O(1) lookup
  const usersMap = useMemo(() => {
    const map = new Map<string, User>();
    if (usersData) {
      Object.values(usersData).forEach((user: User) => {
        map.set(String(user.id), user);
      });
    }
    return map;
  }, [usersData]);

  const columns: ColumnDef<PlatformEvent, any>[] = useMemo(() => {
    const columnDefs: ColumnDef<PlatformEvent, any>[] = [];

    if (!isCompact) {
      columnDefs.push(
        columnHelper.accessor("newHire", {
          id: "new_hire_journey_id",
          header: "New Hire",
          cell: renderNewHireCell,
          enableSorting: false,
          size: 30,
          maxSize: 30,
        }),
      );
    }

    columnDefs.push(
      columnHelper.accessor("occurredAt", {
        id: "occurred_at",
        header: "When",
        cell: (info) => renderOccurredAtCell(info, timeTextColor, invalidDateTextColor),
        enableSorting: false,
        size: isCompact ? 120 : 100,
      }),
      columnHelper.accessor("eventName", {
        id: "details",
        header: "Details",
        cell: (info) => renderDetailsCell(info, usersMap, issueDetailsMap, linkColor, showNewHireReference),
        enableSorting: false,
        size: isCompact ? 360 : 400,
        minSize: 200,
      }),
    );

    if (!isCompact) {
      columnDefs.push(
        columnHelper.accessor("entityType", {
          id: "entity_type",
          header: "Item",
          cell: (info) => renderEntityCell(info, entityNameOverrides, issueDetailsMap, linkColor),
          enableSorting: false,
          size: 250,
          minSize: 150,
        }),
      );
    }

    return columnDefs;
  }, [
    entityNameOverrides,
    invalidDateTextColor,
    isCompact,
    issueDetailsMap,
    linkColor,
    timeTextColor,
    showNewHireReference,
    usersMap,
  ]);

  const hasActiveFilters = !!(
    filters.eventType ||
    filters.eventName ||
    filters.startDate ||
    filters.endDate ||
    filters.newHireJourneyId ||
    filters.actorId
  );

  const emptyStateComponent = isCompact ? (
    <VStack spacing={2} p={4} textAlign="center">
      <CDS.Text12 fontWeight="medium" color="fg.default">
        No history yet
      </CDS.Text12>
      <CDS.Text10 color="fg.muted">
        Activity will appear here as it happens.
      </CDS.Text10>
    </VStack>
  ) : (
    <VStack spacing={3} p={8} textAlign="center">
      {hasActiveFilters ? (
        <>
          <CDS.Text14 fontWeight="medium" color="fg.default">
            No events match your filters
          </CDS.Text14>
          <CDS.Text12 color="fg.muted">
            Try adjusting your filters or clearing them to see all events.
          </CDS.Text12>
        </>
      ) : (
        <>
          <CDS.Text14 fontWeight="medium" color="fg.default">
            No events found
          </CDS.Text14>
          <CDS.Text12 color="fg.muted">
            Events will appear here as activities occur across your organization&apos;s onboarding journeys.
          </CDS.Text12>
        </>
      )}
    </VStack>
  );

  const tableProps: GenericTableProps<PlatformEvent> = {
    fetchData: fetchDataWithTransformedSort,
    queryKey: (queryStr: string) =>
      queryStr ? [...platformEventKeys.lists(), queryStr] : [...platformEventKeys.lists()],
    additionalQueries,
    columns,
    meta,
    emptyStateComponent,
    pageSizeOptions: isCompact ? [10, 20, 50] : [20, 50, 100],
    sx: {
      bg: "transparent",
      tableLayout: "fixed",
      width: "100%",
      th: {
        color: headerTextColor,
        fontWeight: "semibold",
        fontSize: isCompact ? "10px" : "xs",
        borderBottom: "1px solid",
        borderBottomColor: headerBorderColor,
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        paddingY: isCompact ? 2 : 3,
        paddingX: isCompact ? 3 : 4,
        bg: "transparent",
        whiteSpace: "normal",
        wordWrap: "break-word",
      },
      td: {
        color: cellTextColor,
        fontSize: isCompact ? "xs" : "sm",
        borderBottom: "1px solid",
        borderBottomColor: cellBorderColor,
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        paddingY: isCompact ? 2 : 3,
        paddingX: isCompact ? 3 : 4,
        bg: "transparent",
        whiteSpace: "normal",
        wordWrap: "break-word",
        overflowWrap: "break-word",
      },
      tbody: {
        bg: "transparent",
      },
      thead: {
        bg: "transparent",
      },
      "tr:last-child td": {
        borderBottom: "none",
      },
      "tr:hover td": {
        bg: hoverRowBg,
      },
    },
    thProps: {
      color: headerTextColor,
      fontWeight: "semibold",
      fontSize: isCompact ? "10px" : "xs",
    },
    containerSx: isCompact ? { minWidth: "unset" } : undefined,
  };

  return <GenericTable {...tableProps} />;
}
